import {type FC, type ReactNode, useCallback, useMemo, useState} from 'react';
import {cn} from '../../../lib/classname';
import {CommonLink} from '../CommonLink';

export type SegmentedControlItem = {
  href?: string;
  content: ReactNode;
  value: string;
};

type Props = {
  className?: string;
  items: SegmentedControlItem[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
};

/**
 * Appleのセグメントコントロールを表現するコンポーネント
 *
 * @reference https://developer.apple.com/design/human-interface-guidelines/segmented-controls
 * - アイテムは1つのみ選択可能
 * - アイテムの幅は内容に関わらず均等になる
 * - valueを指定しない場合はuncontrolledとなる
 *
 * @param {value} props.value 選択されているアイテムのvalue (controlled)
 *
 * @param {defaultValue} props.defaultValue はじめに選択するアイテムのvalue (uncontrolled)
 *
 * @param {onChange} props.onChange アイテムが選択されたときのコールバック
 *
 * @param {SegmentedControlItem} props.items[] セグメントのアイテム
 * - hrefを指定するとLink(Next.js)になる。指定しない場合はbuttonになる。
 */
export const SegmentedControls: FC<Props> = ({
  className,
  items,
  defaultValue,
  value,
  onChange,
}) => {
  const [_selectedValue, _setSelectedValue] = useState(
    defaultValue ?? items[0]?.value,
  );
  const selectedValue = value ?? _selectedValue;

  const handleSegmentClick = useCallback(
    (value: string) => {
      _setSelectedValue(value);
      onChange?.(value);
    },
    [onChange],
  );

  const selectedIndex = useMemo(
    () => items.findIndex((segment) => segment.value === selectedValue),
    [items, selectedValue],
  );

  return (
    <div
      className={cn(
        'relative flex overflow-hidden rounded-[7px] bg-background p-[2px] transition-all',
        className,
      )}
    >
      {items.map((item, index) => {
        const {content, value, href} = item;
        const isSelected = selectedValue === value;
        const isLast = index === items.length - 1;
        const className = cn(
          'relative z-10 px-[10px] py-[3px] flex flex-1 justify-center items-center',
          'text-[13px] text-black text-center truncate',
          'focus-visible:outline-none focus-visible:ring-2 ring-primary ring-inset',
          isSelected && 'font-[590]',
        );
        if (href) {
          return (
            <CommonLink
              key={value}
              href={href}
              onClick={() => {
                handleSegmentClick(value);
              }}
              className={className}
            >
              <span className="truncate">{content}</span>
              <VerticalLine
                isLast={isLast}
                selectedIndex={selectedIndex}
                index={index}
              />
            </CommonLink>
          );
        }

        return (
          <button
            key={value}
            type="button"
            onClick={() => {
              handleSegmentClick(value);
            }}
            className={className}
          >
            <span className="truncate">{content}</span>
            <VerticalLine
              isLast={isLast}
              selectedIndex={selectedIndex}
              index={index}
            />
          </button>
        );
      })}
      <span
        className="absolute inset-y-[2px] z-0 rounded-[7px] bg-white shadow-md transition-transform"
        style={{
          transform: `translateX(calc(${selectedIndex * 100}% + ${selectedIndex * 4}px))`,
          width: `calc(100% / ${items.length} - 4px)`,
        }}
      />
    </div>
  );
};

const VerticalLine: FC<{
  isLast: boolean;
  selectedIndex: number;
  index: number;
}> = ({isLast, selectedIndex, index}) => {
  if (isLast) {
    return null;
  }

  if (selectedIndex === index || selectedIndex === index + 1) {
    return null;
  }

  return (
    <span className="absolute top-1/2 -translate-y-1/2 right-0 border-l-[1px] border-[#8e8e93] border-opacity-30 h-[12px]" />
  );
};
